import type { connectorFactory } from 'components/app/connectorFactory'

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { helpersFunc } from 'config/helpers'

import type { AppProfile } from './app.types'

export type AppExportConnector = {
	profilesExport: {
		extractLinkedInContacts: (
			source: unknown,
			metadata?: object
		) => Promise<unknown>
		extractFullLinkedInContacts: (
			source: unknown,
			metadata?: object
		) => Promise<unknown>
		startCSV: (source: unknown, metadata?: object) => Promise<unknown>
		start: (source: unknown, metadata?: object) => Promise<unknown>
		stop: (metadata?: object) => Promise<unknown>
		clear: (metadata?: object) => Promise<unknown>
		getState: () => unknown
		addExportStartingHandler: (handler: () => void) => void
		addExportStartedHandler: (handler: () => void) => void
		addExportChangedHandler: (handler: (e?: unknown) => void) => void
		addExportFailedHandler: (handler: () => void) => void
		addExportCompletedHandler: (handler: () => void) => void
		removedAllHandlers: () => void
		linkedInSearch: {
			isValid: (url: string) => boolean
			count: (url: string, metadata?: object) => Promise<unknown>
			start: (url: string, metadata?: object) => Promise<void>
		}
		linkedInGroup: {
			isValid: (url: string) => boolean
			count: (url: string, metadata?: object) => Promise<unknown>
			start: (url: string, metadata?: object) => Promise<void>
		}
		linkedInEvent: {
			isValid: (url: string) => boolean
			count: (url: string, metadata?: object) => Promise<unknown>
			start: (url: string, metadata?: object) => Promise<void>
		}
		salesNavigator: {
			isValid: (url: string) => boolean
			count: (url: string, metadata?: object) => Promise<unknown>
			start: (url: string, metadata?: object) => Promise<void>
		}
		linkedInMyConnections: {
			count: (url: string, metadata?: object) => Promise<unknown>
			start: (url: string, metadata?: object) => Promise<void>
		}
	}
}

interface AppState {
	appProfile: null | Partial<AppProfile>
	appProfileDone: boolean
	associatedLinkedInProfile: boolean
	auth: 'auth' | 'loading' | 'none'
	connector: ReturnType<typeof connectorFactory>
	appExportConnector: null | AppExportConnector
	errorRequest: ErrorRequest

	isInstalledDone: boolean

	linkedInAuth: boolean
	linkedProfile: any
	linkedProfileDone: boolean
	pageContentLoader: boolean
	pageModalLoader: boolean
	subscription: any
	tableContentLoader: boolean
	token: string
}

interface ErrorRequest {
	activityQueue: boolean
	campaigns: boolean
	campaignsTeams: boolean
	contacts: boolean
}

const initialState: AppState = {
	appProfile: {},
	appProfileDone: false,
	associatedLinkedInProfile: true,
	auth: 'loading',
	// @ts-ignore init work only in App.
	// TODO: Maybe better to replace into separate context
	connector: {},
	appExportConnector: null,
	errorRequest: {
		activityQueue: false,
		campaigns: false,
		campaignsTeams: false,
		contacts: false
	},
	isInstalledDone: false,
	linkedInAuth: true,
	linkedProfile: {},
	linkedProfileDone: false,
	pageContentLoader: true,
	pageModalLoader: false,
	subscription: null,
	tableContentLoader: false,
	token: ''
}

const selectPersona = (state: AppState) =>
	state.appProfile?.impersonatedPersona ?? state.appProfile

const appSlice = createSlice({
	initialState,
	name: 'app',
	reducers: {
		appAuth(state, action: PayloadAction<AppState['auth']>) {
			state.auth = action.payload
		},

		changePageContentLoaderStatus(
			state,
			action: PayloadAction<AppState['pageContentLoader']>
		) {
			state.pageContentLoader = action.payload
		},
		changePageModalLoader(state, action: PayloadAction<boolean>) {
			state.pageModalLoader = action.payload
		},

		changeTableContentLoaderStatus(state, action: PayloadAction<boolean>) {
			state.tableContentLoader = action.payload
		},

		createConnector: (state, action) => {
			state.connector = action.payload
		},
		setAppExportConnector: (
			state,
			action: PayloadAction<AppState['appExportConnector']>
		) => {
			state.appExportConnector = action.payload
		},
		setAppProfile: (
			state,
			action: PayloadAction<Pick<AppState, 'appProfile' | 'appProfileDone'>>
		) => {
			state.appProfile = action.payload.appProfile
			state.appProfileDone = action.payload.appProfileDone
		},

		setAssociatedLinkedInProfile(state, action: PayloadAction<boolean>) {
			state.associatedLinkedInProfile = action.payload
		},

		setLinkedProfile: (
			state,
			action: PayloadAction<
				Pick<AppState, 'linkedProfile' | 'linkedProfileDone'>
			>
		) => {
			state.linkedProfile = action.payload.linkedProfile
			state.linkedProfileDone = action.payload.linkedProfileDone
		},

		setSubscriptions(state, action: PayloadAction<any>) {
			state.subscription = action.payload
		},

		updateErrorRequest(state, action: PayloadAction<ErrorRequest>) {
			const old = state.errorRequest
			state.errorRequest = {
				...old,
				...action.payload
			}
		},

		updateInstalled(state, action: PayloadAction<boolean>) {
			state.isInstalledDone = action.payload
		}
	},
	selectors: {
		appProfile: state => state.appProfile,
		appProfileDone: state => state.appProfileDone,
		auth: state => state.auth,
		connector: state => state.connector,
		appExportConnector: state => state.appExportConnector,
		errorRequest: state => state.errorRequest,
		fullName(state) {
			const { familyName = '', givenName = '' } = selectPersona(state) ?? {}

			return `${givenName} ${familyName}`
		},
		isBackendAutomation: state =>
			selectPersona(state)?.isBackendAutomation ?? false,
		isBetaUser: state => state.appProfile?.isBetaUser || false,
		isImpersonated: state => Boolean(state.appProfile?.impersonatedPersona),
		isInstalled: state => {
			try {
				return state.connector.extension?.isInstalled() ?? true
				// ! Something messed with flags. If we remove isInstalledDone no auth widget showed before update profile
			} catch (e) {
				helpersFunc.logEnjectServicePromise(e)
				return false
			}
		},
		isInstalledDone: state => state.isInstalledDone,
		isPro: state => state.appProfile?.isProPlanAccessible,
		isReadyConnector: state => {
			const keys = Object.keys(state.connector)
			return keys.length > 0
		},
		isUpgraded: ({ appProfile }) =>
			appProfile?.impersonatedPersona ||
			(appProfile?.subscriptionIsActive &&
				appProfile?.subscriptionOwnerId &&
				appProfile.subscriptionOwnerId !== appProfile?.personaId),
		linkedProfile: state => state.linkedProfile,
		linkedProfileDone: state => state.linkedProfileDone,
		notLoggedLI: app => !(app.linkedInAuth && app.associatedLinkedInProfile),
		persona: selectPersona,
		personIdLi: state => selectPersona(state)?.linkedInProfile?.entityId ?? '',
		refreshAccessToken: state => state.connector.auth.refreshAccessToken,
		signInUnderTeamMember: state => state.connector.auth.signInUnderTeamMember,
		subscription: state => state.subscription,
		subscriptionIsActive: state => state.appProfile?.subscriptionIsActive,
		timezoneOffset: state => state.appProfile?.timezoneOffset,
		token: state => state.token
	}
})

const appActions = appSlice.actions
const appSelectors = appSlice.selectors
export { appActions, appSelectors, appSlice }
export type AuthStatus = AppState['auth']
